import React from 'react';
import { Box, Flex, Text } from '@chatsdet/ui';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { CopyButton } from '../CopyButton';

export const formatJsonDisplay = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    try {
      return JSON.stringify(JSON.parse(trimmed), null, 2);
    } catch {
      return trimmed;
    }
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

export const JsonBlock: React.FC<{
  title: string;
  value: unknown;
  emptyText: string;
  headerRight?: React.ReactNode;
}> = ({ title, value, emptyText, headerRight }) => {
  const text = formatJsonDisplay(value);
  const [expanded, setExpanded] = React.useState(false);
  const lineCount = text ? text.split('\n').length : 0;
  const isLong = lineCount > 8;

  return (
    <Flex direction="column" gap="sm">
      <Flex align="center" justify="between">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          {title}
        </span>
        {headerRight}
      </Flex>
      {text ? (
        <div className="relative group/json">
          <CopyButton value={text} label={title.toLowerCase()} />
          <div className="relative overflow-hidden rounded-md border border-slate-800">
            <Box
              className={`p-3 bg-slate-950 overflow-auto transition-all ${
                isLong && !expanded ? 'max-h-32' : 'max-h-80'
              }`}
            >
              <SyntaxHighlighter
                language="json"
                style={vscDarkPlus}
                customStyle={{ margin: 0, background: 'transparent' }}
                wrapLongLines
              >
                {text}
              </SyntaxHighlighter>
            </Box>
            {isLong && !expanded && (
              <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-slate-950 to-transparent" />
            )}
          </div>
          {isLong && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="mt-1 text-xs text-primary hover:underline"
            >
              {expanded ? 'Show less' : 'Show more'}
            </button>
          )}
        </div>
      ) : (
        <Text className="text-muted-foreground text-sm">{emptyText}</Text>
      )}
    </Flex>
  );
};
